import { format } from 'date-fns'
import { TOOLTIP } from '../../../../constants/tooltips'
import { DecommissioningFieldsEdit, FieldTypes } from '../../../../types/form-types'
import { ValidationErrors } from '../../../hooks/use-edit-form'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import { CheckableField, DateField, FormField, InputWidth, TextInput } from '@msaf/core-react'

interface WellConstructionDecommissioningEditInterface {
  fields: Partial<DecommissioningFieldsEdit>
  isLoading: boolean
  setValue: (key: keyof DecommissioningFieldsEdit, value: FieldTypes) => void
  validationErrors: ValidationErrors<DecommissioningFieldsEdit>
}

export default function WellConstructionDecommissioningEdit({
  fields,
  isLoading,
  setValue,
  validationErrors,
}: WellConstructionDecommissioningEditInterface) {
  return (
    <>
      <FormField
        validationMessages={validationErrors?.decommissionedDate}
        isSkeleton={isLoading}
        label='Decommissioned date'
        labelId='decommissionedDateLabel'
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.decommissionedDate)}
          setSelectedDate={(date: Date) =>
            setValue('decommissionedDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')
          }
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.decommissionedByCompany}
        isSkeleton={isLoading}
        label='Decommissioned by'
        labelId='decommissionedByCompanyLabel'
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='decommissionedByCompany'
          labelledBy='decommissionedByCompanyLabel'
          fieldFilters={{ councilId: fields.councilId ?? '' }}
          value={`${fields.decommissionedByCompany ?? ''}`}
          setValue={({ value }) => setValue('decommissionedByCompany', value)}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.decommissioningDetails}
        validationMessages={validationErrors?.decommissioningDetails}
        isSkeleton={isLoading}
        label='Decommissioning details'
        htmlFor='decommissioningDetails'
      >
        <TextInput
          isSkeleton={isLoading}
          id='decommissioningDetails'
          value={`${fields.decommissioningDetails ?? ''}`}
          setValue={(value) => setValue('decommissioningDetails', value)}
        />
      </FormField>
      <FormField isSkeleton={isLoading} label='Decommissioning method' labelId='decommissioningMethodLabel'>
        <LookupField
          isSkeleton={isLoading}
          fieldId='decommissioningMethod'
          labelledBy='decommissioningMethodLabel'
          value={`${fields.decommissioningMethod ?? ''}`}
          setValue={({ value }) => setValue('decommissioningMethod', value)}
        />
      </FormField>
      <FormField isSkeleton={isLoading} label='Decommissioning verified' labelId='recordVerifiedByDateLabel'>
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.recordVerifiedByDate)}
          setSelectedDate={(date: Date) =>
            setValue('recordVerifiedByDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')
          }
        />
      </FormField>
      <FormField isSkeleton={isLoading} label='Compliance' labelId='complianceLabel'>
        <CheckableField
          id='isCompliantWithNzStandards'
          name='isCompliantWithNzStandards'
          label='Is compliant with NZ standards'
          describedBy='complianceLabel'
          isSkeleton={isLoading}
          type='checkbox'
          checked={Boolean(fields.isCompliantWithNzStandards)}
          onChange={(e) => setValue('isCompliantWithNzStandards', e.target.checked)}
        />
      </FormField>
      <FormField isSkeleton={isLoading} label='Additional comments' htmlFor='decommissioningMethodsAdditionalComments'>
        <TextInput
          isSkeleton={isLoading}
          id='decommissioningMethodsAdditionalComments'
          value={`${fields.decommissioningMethodAdditionalComments ?? ''}`}
          setValue={(value) => setValue('decommissioningMethodAdditionalComments', value)}
        />
      </FormField>
    </>
  )
}
